import React from "react";
import classNames from "classnames";
import Link from "next/link";
// eslint-disable-next-line no-restricted-imports -- TODO: update to `next/navigation` before using this component in App Router
import { useRouter } from "next/router";

import { STRAIN_USER_REVIEW_DISCLAIMER } from "@/constants/disclaimers";

import Switch from "@/components/botanic/Switch/Switch";
import ExpandableFilterOptions from "@/components/ExpandableFilterOptions";
import { FilterSection } from "@/components/Filters/FilterSection";
import IconSvg from "@/components/IconSvg";
import MedicalDisclaimers from "@/components/MedicalDisclaimers";
import { useIcons } from "@/components/MenuFilters/hooks";
import { MenuItemFilterSection } from "@/components/MenuFilters/types/MenuFilters";

import TileLink from "../../botanic/TileLInk";
import CheckboxSearchList from "../CheckboxSearchList";
import { getUpdatedFilterHref } from "../utils/filterUtils";
import useQueryFilterValues from "./hooks/useQueryFilterValues";
import {
  AvailableFilter,
  FilterName,
  FilterType,
  GroupFilter,
  ToggleFilterType,
} from "./types";

const DEFAULT_VISIBLE_TAKE = 9;

export type NoFollowLinkProp = (
  href: ReturnType<typeof getUpdatedFilterHref>,
) => boolean;

const DynamicFilters: React.FC<{
  availableFilters: AvailableFilter[];
  defaultOpenKey?: string;
  sectionPadding?: string;
  noFollowLink?: NoFollowLinkProp;
}> = ({ availableFilters, defaultOpenKey, sectionPadding, noFollowLink }) => (
  <>
    {availableFilters.map((filter) => (
      <DynamicFilter
        key={filter.name}
        filter={filter}
        defaultOpenKey={defaultOpenKey}
        sectionPadding={sectionPadding}
        noFollowLink={noFollowLink}
      />
    ))}
  </>
);

export const DynamicFilter: React.FC<{
  filter: AvailableFilter;
  defaultOpenKey?: string;
  openSection?: string;
  sectionPadding?: string;
  noFollowLink?: NoFollowLinkProp;
}> = ({ filter, defaultOpenKey, sectionPadding, noFollowLink }) => (
  <>
    {filter.type === FilterType.toggle && (
      <ToggleFilter
        filter={filter as ToggleFilterType}
        sectionPadding={sectionPadding}
        noFollowLink={noFollowLink}
      />
    )}
    {filter.type === FilterType.tileGroup &&
      (filter as GroupFilter).values.length > 0 && (
        <TileFilters
          filter={filter as GroupFilter}
          defaultOpenKey={defaultOpenKey}
          sectionPadding={sectionPadding}
          noFollowLink={noFollowLink}
        />
      )}
    {filter.type === FilterType.checkBoxGroup &&
      (filter as GroupFilter).values.length > 0 && (
        <CheckboxFilters
          filter={filter as GroupFilter}
          defaultOpenKey={defaultOpenKey}
          sectionPadding={sectionPadding}
          noFollowLink={noFollowLink}
        />
      )}
  </>
);

const ToggleFilter: React.FC<{
  filter: ToggleFilterType;
  sectionPadding?: string;
  noFollowLink?: NoFollowLinkProp;
}> = ({ filter, sectionPadding, noFollowLink }) => {
  const {
    name,
    label,
    sublabel,
    filterIcon,
    labelIcon,
    count = 0,
    showCount = false,
  } = filter;
  const { asPath, push } = useRouter();
  const [appliedValue] = useQueryFilterValues(name);

  const { getIconPath } = useIcons();
  const disabled = count < 1;

  const labelIconClassnames = classNames("self-center", {
    "text-verified": name === MenuItemFilterSection.BrandVerified,
  });

  if (disabled) {
    return null;
  }

  const href = getUpdatedFilterHref(asPath, name, "true");

  return (
    <div
      className={classNames(
        "flex flex-col border-t border-b border-light-grey py-[17px] md:gap-2",
        {
          "md:pb-lg": sublabel,
          "p-lg": sectionPadding,
        },
      )}
    >
      <Link
        href={href}
        rel={noFollowLink?.(href) ? "nofollow" : undefined}
        className="flex justify-between"
        data-testid="toggle-filter"
      >
        <span className={classNames("flex font-bold")}>
          {filterIcon && (
            <IconSvg
              height="24"
              width="24"
              className="flex-shrink-0 inline mr-xs"
              svgClassName="fill-[var(--color-notification)]"
              filePath={getIconPath(name as MenuItemFilterSection, filterIcon)}
              testId={`${name}-filter-icon`}
            />
          )}
          {label}&nbsp;
          {labelIcon && (
            <IconSvg
              height="16"
              width="16"
              className={labelIconClassnames}
              filePath={getIconPath(name as MenuItemFilterSection, labelIcon)}
            />
          )}
          {showCount && (
            <span className="text-grey text-start leading-none self-center">
              ({count.toLocaleString("en-us")})
            </span>
          )}
        </span>
        <Switch
          checked={appliedValue === "true"}
          checkedBgColor="bg-notification"
          disabled={disabled}
          screenReaderText={label}
          onChange={() => {
            push(href, undefined, { shallow: false });
          }}
        />
      </Link>
      {sublabel && (
        <div className="flex items-center">
          <span className="text-xs font-normal whitespace-pre-wrap text-left text-grey">
            {sublabel}
          </span>
        </div>
      )}
    </div>
  );
};

const CheckboxFilters: React.FC<{
  filter: GroupFilter;
  defaultOpenKey: string | undefined;
  sectionPadding?: string;
  noFollowLink?: NoFollowLinkProp;
}> = ({ filter, defaultOpenKey, sectionPadding, noFollowLink }) => {
  const { asPath } = useRouter();
  const selectedValues: string[] = useQueryFilterValues(filter.name);

  return (
    <FilterSection
      title={filter.label}
      numberOfItemsSelected={selectedValues.length}
      openDefault={defaultOpenKey === filter.name}
      open={defaultOpenKey === filter.name}
      sectionPadding={sectionPadding}
    >
      {filter.name === "helps_with" && (
        <MedicalDisclaimers usDisclaimer={STRAIN_USER_REVIEW_DISCLAIMER} />
      )}
      <CheckboxSearchList
        filter={filter}
        currentPath={asPath}
        noFollowLink={noFollowLink}
      />
    </FilterSection>
  );
};

const TileFilters: React.FC<{
  filter: GroupFilter;
  defaultOpenKey: string | undefined;
  visibleTake?: number;
  sectionPadding?: string;
  noFollowLink?: NoFollowLinkProp;
}> = ({
  filter,
  defaultOpenKey,
  visibleTake = DEFAULT_VISIBLE_TAKE,
  sectionPadding,
  noFollowLink,
}) => {
  const { values, name, label } = filter;
  const selectedValues: string[] = useQueryFilterValues(filter.name);

  const visibleOptions = values.slice(0, visibleTake);
  const hiddenOptions = values.slice(visibleTake);

  return (
    <FilterSection
      title={label}
      numberOfItemsSelected={selectedValues.length}
      openDefault={defaultOpenKey === name}
      open={defaultOpenKey === name}
      sectionPadding={sectionPadding}
    >
      <div className="row" data-testid="tile-filters">
        <div className="flex flex-wrap overflow-auto max-h-80">
          <DynamicTileFilter
            filter={{ ...filter, values: visibleOptions }}
            noFollowLink={noFollowLink}
          />
          {!!hiddenOptions.length && (
            <ExpandableFilterOptions
              expandButtonText={`Show all ${values.length.toLocaleString(
                "en-us",
              )}`}
              className="ml-sm"
            >
              <DynamicTileFilter
                filter={{ ...filter, values: hiddenOptions }}
                noFollowLink={noFollowLink}
              />
            </ExpandableFilterOptions>
          )}
        </div>
      </div>
    </FilterSection>
  );
};

const DynamicTileFilter: React.FC<{
  filter: GroupFilter;
  noFollowLink?: NoFollowLinkProp;
}> = ({ filter, noFollowLink }) => {
  const { name, showCount, values } = filter;
  const selectedValues: string[] = useQueryFilterValues(name);
  const { asPath } = useRouter();

  let rootDir = "";
  switch (name) {
    case FilterName.StrainEffects:
      rootDir = "effects";
      break;
    case FilterName.StrainCategory:
      rootDir = "phenotypes";
      break;
    default:
      break;
  }
  const root = rootDir ? `${rootDir}/` : "";

  return (
    <>
      {values?.map(({ label, value, count }) => {
        const href = getUpdatedFilterHref(asPath, name, value);

        return (
          <div className="col-1/3 mb-sm" key={value}>
            <TileLink
              href={href}
              rel={noFollowLink?.(href) ? "nofollow" : undefined}
              iconPath={`${root}${label.replace(/_/g, "-").toLowerCase()}.svg`}
              label={label}
              lowercase
              count={count}
              showCount={showCount}
              selected={selectedValues.includes(value)}
            />
          </div>
        );
      })}
    </>
  );
};

export default DynamicFilters;
